import { useMemo } from 'react';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { useCoachPermissions } from 'bundles/ai-course-coach/utils/CoachPermissionWrapper';
import { getGlobalAppName } from 'bundles/ai-course-coach/utils/getGlobalAppName';
import { isCoachEnabledByApp } from 'bundles/ai-course-coach/utils/isCoachEnabledByApp';
import SessionStore from 'bundles/course-sessions/stores/SessionStore';
import leeCoachEpicClient from 'bundles/epic/clients/leeCoach';
import { createFeatureScope } from 'bundles/product-features';

// Feature flags for Product Features System
// https://coursera.atlassian.net/wiki/spaces/ENT/pages/1327235526/Product+Features+Developer+Guide
export const { ProductFeaturesQuery, useProductFeatures } = createFeatureScope({
  enterprise: {
    enableAiCoach: true /* value is unused */,
    enableAiCoachEarlyAccess: true /* value is unused */,
  },
  degrees: {
    enableAiCoachForDegrees: true /* value is unused */,
  },
});

// https://tools.coursera.org/epic/experiment/oxFjT2Y5EfCU9Qr_ypib8Q
export const isCoachDisabledForCourseBranch = (courseBranchId: string): boolean => {
  const disabledCourses = leeCoachEpicClient.get('disableCoachTutorForCourse');
  if (disabledCourses && typeof disabledCourses === 'object') {
    return disabledCourses[courseBranchId] === true;
  }
  return false;
};

// https://tools.coursera.org/epic/experiment/4M4n2plsEfCAfhJYNo68EQ
export const isCoachEnabledForCourseBranch = (courseBranchId: string): boolean => {
  const enabledCourses = leeCoachEpicClient.get('enableCoachTutorForCourse');

  if (enabledCourses && typeof enabledCourses === 'object') {
    return enabledCourses[courseBranchId] === true;
  }
  return false;
};

/**
 * EPIC check to enable Coach Tutor GA Rollout
 * https://tools.coursera.org/epic/experiment/0QB4bHobEfCgRA4nU0VIYQ -> this is the new one going forward with no holdout
 */
export const isCoachTutorGAEpic = ({ epicImpression = false }: { epicImpression?: boolean } = {}): boolean => {
  if (epicImpression) {
    return leeCoachEpicClient.get('enableCoachTutorRollout');
  }
  return leeCoachEpicClient.preview('enableCoachTutorRollout');
};

export const useIsCoachDisabledForCourseBranch = (): boolean => {
  const { courseBranchId } = useFluxibleStore(SessionStore, (store) => {
    return {
      courseBranchId: typeof store.getBranchId === 'function' ? store.getBranchId() : undefined,
    };
  });

  return courseBranchId ? isCoachDisabledForCourseBranch(courseBranchId) : false;
};

// [COACH-1836] allows us to force show Coach if enabled for course branch
export const useIsCoachEnabledForCourseBranch = (): boolean => {
  const { courseBranchId } = useFluxibleStore(SessionStore, (store) => {
    return {
      courseBranchId: typeof store.getBranchId === 'function' ? store.getBranchId() : undefined,
    };
  });

  return courseBranchId ? isCoachEnabledForCourseBranch(courseBranchId) : false;
};

export const useCoachEpicEnabledState = ({
  courseId = '',
  epicImpression = false,
}: {
  courseId?: string; // courseId is optional here because useCoachEpicEnabledState is used in ProgramHomeWrapper and EnterpriseHeaderFooterWrapper which don't have courseId. This should be okay because neither seem to actually call useCoachEpicEnabledState to render Coach.
  epicImpression?: boolean;
} = {}) => {
  const appName = useMemo(() => getGlobalAppName(), []);
  const coachIncludedByRoute = useMemo(() => isCoachEnabledByApp(appName), [appName]);

  // pass in if there should be an impression triggered in the event the epic is called. default false
  const { showEmbeddedCoach, isLoading } = useCoachPermissions({ courseId, epicImpression });

  return useMemo(
    () => ({
      showEmbeddedCoach: coachIncludedByRoute && showEmbeddedCoach,
      isLoading,
    }),
    [showEmbeddedCoach, coachIncludedByRoute, isLoading]
  );
};

export const isIntentionRoutingEnabled = (): boolean => {
  return leeCoachEpicClient.get('enableIntentionRouting');
};

// https://tools.coursera.org/epic/experiment/nUpS7ke9EfCVowr_5BhkRQ
export const isContinuousChatEnabled = ({ epicImpression = false }: { epicImpression?: boolean } = {}): boolean => {
  if (epicImpression) {
    return leeCoachEpicClient.get('enableContinuousChat');
  }
  return leeCoachEpicClient.preview('enableContinuousChat');
};

// AB experiment for Dynamic Prompts
// https://tools.coursera.org/epic/experiment/bIEPp4g_EfCAfhJYNo68EQ
export const isDynamicPromptsEnabled = ({
  courseId,
  epicImpression = false,
}: {
  courseId: string;
  epicImpression?: boolean;
}) => {
  if (epicImpression) {
    return leeCoachEpicClient.get('enableDynamicPrompts', { course_id: courseId });
  }
  return leeCoachEpicClient.preview('enableDynamicPrompts', { course_id: courseId });
};

export const isPersonalizedWelcomeEnabled = (): boolean => {
  // TODO: replace with actual epic check when available
  return false;
};
